import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/images`;

/** Limite de upload da API (5 MB) */
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Biblioteca de imagens da empresa (ImagesController - /images)
 * ImagesDTO: { id, name, url, company_code }. URL única por empresa.
 */
class ImageService {
  async getAll(search) {
    try {
      const response = await api.get(API_BASE, {
        params: search ? { search } : undefined,
      });
      return unwrapList(response);
    } catch (error) {
      console.error("❌ Erro ao buscar imagens:", error);
      throw error;
    }
  }

  async getById(id) {
    try {
      const response = await api.get(`${API_BASE}/${id}`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar imagem ${id}:`, error);
      throw error;
    }
  }

  /**
   * Envia um arquivo para a biblioteca da empresa
   * @param {File} file
   * @param {string} [name]
   * @param {(percent: number) => void} [onProgress]
   * @param {string} [companyCode] - empresa destino (só vale para o owner)
   * @returns {Promise<object|null>} ImagesDTO
   */
  async upload(file, name, onProgress, companyCode) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (name) formData.append("name", name);
      if (companyCode) formData.append("company_code", companyCode);
      const response = await api.post(`${API_BASE}/upload`, formData, {
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      });
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao enviar imagem:", error);
      throw error;
    }
  }

  /** Cadastra uma imagem já hospedada (URL http/https) */
  async createFromUrl(name, url) {
    try {
      const response = await api.post(API_BASE, { name, url });
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao cadastrar imagem:", error);
      throw error;
    }
  }

  async update(id, data) {
    try {
      const response = await api.put(`${API_BASE}/${id}`, data);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar imagem ${id}:`, error);
      throw error;
    }
  }

  async delete(id) {
    try {
      await api.delete(`${API_BASE}/${id}`);
    } catch (error) {
      console.error(`❌ Erro ao excluir imagem ${id}:`, error);
      throw error;
    }
  }

  /**
   * Valida tipo e tamanho antes do envio
   * @param {File} file
   * @returns {string|null} mensagem de erro ou null
   */
  validateFile(file) {
    if (!file) return "Selecione um arquivo";
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return "Formato não suportado. Use JPG, PNG ou WEBP.";
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return "A imagem deve ter no máximo 5 MB.";
    }
    return null;
  }
}

export default new ImageService();
